import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchHostExploitRules,
  addHostExploitExclusion,
  removeHostExploitExclusion,
} from '../../api/exploit-rules';
import type { ExploitRuleHostConfig, ExploitBlockRuleWithStatus } from '../../types/exploit-rules';
import { HostCategoryRulesSection } from './HostCategoryRulesSection';

interface HostExploitRulesModalProps {
  host: ExploitRuleHostConfig;
  onClose: () => void;
}

export function HostExploitRulesModal({ host, onClose }: HostExploitRulesModalProps) {
  const { t, i18n } = useTranslation('exploitRules');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDisabledOnly, setShowDisabledOnly] = useState(false);
  const queryClient = useQueryClient();

  const rulesQuery = useQuery({
    queryKey: ['host-exploit-rules', host.proxy_host_id],
    queryFn: () => fetchHostExploitRules(host.proxy_host_id),
  });

  const disableMutation = useMutation({
    mutationFn: ({ ruleId, reason }: { ruleId: string; reason?: string }) =>
      addHostExploitExclusion(host.proxy_host_id, ruleId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host-exploit-rules', host.proxy_host_id] });
      queryClient.invalidateQueries({ queryKey: ['exploit-rule-hosts'] });
    },
  });

  const enableMutation = useMutation({
    mutationFn: (ruleId: string) => removeHostExploitExclusion(host.proxy_host_id, ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host-exploit-rules', host.proxy_host_id] });
      queryClient.invalidateQueries({ queryKey: ['exploit-rule-hosts'] });
    },
  });

  // State for disable reason modal
  const [disableModalRule, setDisableModalRule] = useState<{
    rule: ExploitBlockRuleWithStatus;
    categoryName: string;
  } | null>(null);
  const [disableReason, setDisableReason] = useState('');

  const handleToggleRule = (rule: ExploitBlockRuleWithStatus, categoryName: string) => {
    if (rule.globally_disabled) return; // Cannot toggle globally disabled rules
    if (!rule.host_disabled) {
      setDisableModalRule({ rule, categoryName });
      setDisableReason('');
    } else {
      enableMutation.mutate(rule.id);
    }
  };

  const handleConfirmDisable = () => {
    if (!disableModalRule) return;
    disableMutation.mutate({
      ruleId: disableModalRule.rule.id,
      reason: disableReason.trim() || undefined,
    });
    setDisableModalRule(null);
    setDisableReason('');
  };

  // Filter rules
  const filteredData = useMemo(() => {
    if (!rulesQuery.data) return { categories: [], totalRules: 0, disabledRules: 0 };

    const query = searchQuery.toLowerCase();
    let totalRules = 0;
    let disabledRules = 0;

    // Handle null categories (when no rules exist)
    if (!rulesQuery.data.categories) {
      return { categories: [], totalRules: 0, disabledRules: 0 };
    }

    const categories = rulesQuery.data.categories
      .map((cat) => {
        let rules = cat.rules || [];

        if (query) {
          rules = rules.filter(
            (r) =>
              r.id.toLowerCase().includes(query) ||
              r.name?.toLowerCase().includes(query) ||
              r.pattern?.toLowerCase().includes(query) ||
              r.description?.toLowerCase().includes(query)
          );
        }

        if (showDisabledOnly) {
          rules = rules.filter((r) => r.host_disabled || r.globally_disabled);
        }

        totalRules += rules.length;
        disabledRules += rules.filter((r) => r.host_disabled || r.globally_disabled).length;

        return { ...cat, rules };
      })
      .filter((cat) => {
        if (activeCategory === 'all') return cat.rules && cat.rules.length > 0;
        return cat.id === activeCategory;
      });

    return { categories, totalRules, disabledRules };
  }, [rulesQuery.data, searchQuery, showDisabledOnly, activeCategory]);

  const isPending = disableMutation.isPending || enableMutation.isPending;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(i18n.language, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="absolute inset-4 md:inset-8 lg:inset-12 bg-white dark:bg-slate-800 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('policyManager.title', { name: host.proxy_host_name })}
            </h2>
            <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
              {t('policyManager.subtitle', { total: rulesQuery.data?.total_rules || 0, disabled: host.exclusion_count })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors text-slate-500 dark:text-slate-400"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex-shrink-0 px-6 py-3 border-b dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t('policyManager.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-slate-700 text-gray-900 dark:text-white dark:placeholder-slate-400"
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showDisabledOnly}
              onChange={(e) => setShowDisabledOnly(e.target.checked)}
              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
            />
            <span className="text-sm text-gray-600 dark:text-slate-300">
              {t('policyManager.showDisabledOnly')}
            </span>
          </label>

          {/* Results count */}
          <div className="text-sm text-gray-500 dark:text-slate-400">
            {t('policyManager.showingCount', { count: filteredData.totalRules })}
            {filteredData.disabledRules > 0 && (
              <span className="text-amber-600 dark:text-amber-400 ml-1">
                {t('policyManager.disabledCount', { count: filteredData.disabledRules })}
              </span>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
          {/* Category Sidebar */}
          <div className="w-full md:w-48 h-auto md:h-full flex-shrink-0 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 overflow-x-auto md:overflow-y-auto flex md:block scrollbar-hide">
            <div className="flex md:block p-2 gap-2 min-w-max">
              <button
                onClick={() => setActiveCategory('all')}
                className={`w-auto md:w-full whitespace-nowrap px-3 py-2 text-left rounded-lg text-sm transition-colors ${activeCategory === 'all'
                  ? 'bg-amber-600 text-white'
                  : 'hover:bg-gray-200 dark:hover:bg-slate-800 text-gray-700 dark:text-slate-300'
                }`}
              >
                {t('policyManager.allCategories')}
              </button>
              {rulesQuery.data?.categories?.map((cat) => {
                const disabledCount = cat.rules?.filter((r) => r.host_disabled || r.globally_disabled).length || 0;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`w-auto md:w-full whitespace-nowrap px-3 py-2 text-left rounded-lg text-sm transition-colors mt-0 md:mt-1 ${activeCategory === cat.id
                      ? 'bg-amber-600 text-white'
                      : 'hover:bg-gray-200 dark:hover:bg-slate-800 text-gray-700 dark:text-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate">{cat.name}</span>
                      {disabledCount > 0 && (
                        <span
                          className={`ml-1 px-1.5 py-0.5 text-xs rounded ${activeCategory === cat.id
                            ? 'bg-amber-500 text-white'
                            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                          }`}
                        >
                          {disabledCount}
                        </span>
                      )}
                    </div>
                    <div className={`text-xs ${activeCategory === cat.id ? 'text-amber-200' : 'text-gray-500 dark:text-slate-500'}`}>
                      {t('policyManager.ruleCount', { count: cat.rule_count })}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rules List */}
          <div className="flex-1 overflow-y-auto">
            {rulesQuery.isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
              </div>
            ) : rulesQuery.error ? (
              <div className="p-6 text-center text-red-600">
                {t('policyManager.loadError')}
              </div>
            ) : filteredData.categories.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-slate-400">
                {searchQuery ? t('policyManager.noResults') : t('policyManager.empty')}
              </div>
            ) : (
              <div className="divide-y">
                {filteredData.categories.map((category) => (
                  <HostCategoryRulesSection
                    key={category.id}
                    category={category}
                    onToggleRule={(rule) => handleToggleRule(rule, category.name)}
                    isPending={isPending}
                    showCategoryHeader={activeCategory === 'all'}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Disable Reason Modal */}
      {disableModalRule && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDisableModalRule(null)}
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b dark:border-slate-700 bg-amber-50 dark:bg-amber-900/20">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('disableModal.title')}</h3>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded text-xs font-mono bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                    {disableModalRule.rule.name}
                  </span>
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                    {disableModalRule.categoryName}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-slate-400">
                  {disableModalRule.rule.description || t('policyManager.noDescription')}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  {t('disableModal.reasonLabel')} <span className="text-gray-400 dark:text-slate-500">({t('common.optional')})</span>
                </label>
                <textarea
                  value={disableReason}
                  onChange={(e) => setDisableReason(e.target.value)}
                  placeholder={t('disableModal.reasonPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-slate-700 text-gray-900 dark:text-white dark:placeholder-slate-400 resize-none"
                  rows={3}
                  autoFocus
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDisableModalRule(null)}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                >
                  {t('disableModal.cancel')}
                </button>
                <button
                  onClick={handleConfirmDisable}
                  disabled={disableMutation.isPending}
                  className="px-4 py-2 text-sm text-white bg-amber-600 dark:bg-amber-500 rounded-lg hover:bg-amber-700 dark:hover:bg-amber-600 transition-colors disabled:opacity-50"
                >
                  {disableMutation.isPending ? t('disableModal.processing') : t('disableModal.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
